import { useCallback, useEffect, useState, type JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import { numberOr } from '../../lib/numbers.ts';
import { api, type Assignment } from '../../api.ts';

/**
 * Settings for one piece of homework.
 *
 * The deadline is entered in the teacher's own timezone and sent as UTC. Showing them a
 * Z-suffixed instant and asking them to do the arithmetic is how deadlines end up hours off.
 */

/** ISO instant → the `YYYY-MM-DDTHH:mm` a datetime-local input expects, in local time. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

export function AssignmentEditorPage(): JSX.Element {
  const { assignmentId } = useParams();
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [due, setDue] = useState('');
  const [allowText, setAllowText] = useState(true);
  const [allowFiles, setAllowFiles] = useState(true);
  const [maxFiles, setMaxFiles] = useState('3');
  const [maxFileMb, setMaxFileMb] = useState('10');
  const [maxAttempts, setMaxAttempts] = useState('0');
  const [latePolicy, setLatePolicy] = useState('allow_marked');

  const load = useCallback((): void => {
    if (!assignmentId) return;
    api.authoring
      .assignment(assignmentId)
      .then((loaded) => {
        setAssignment(loaded);
        setTitle(loaded.title);
        setDescription(loaded.description_md);
        setDue(toLocalInput(loaded.due_at));
        setAllowText(loaded.allow_text);
        setAllowFiles(loaded.allow_files);
        setMaxFiles(String(loaded.max_files));
        setMaxFileMb(String(loaded.max_file_mb));
        setMaxAttempts(String(loaded.max_attempts));
        setLatePolicy(loaded.late_policy);
      })
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'Не удалось загрузить задание'),
      );
  }, [assignmentId]);

  useEffect(load, [load]);

  async function save(): Promise<void> {
    if (!assignmentId) return;
    setBusy(true);
    setError('');
    setSaved(false);

    try {
      await api.authoring.updateAssignment(assignmentId, {
        title: title.trim(),
        description_md: description,
        due_at: due === '' ? null : new Date(due).toISOString(),
        allow_text: allowText,
        allow_files: allowFiles,
        // The defaults are the ones printed beside each box, so an empty one saves what the
        // screen says it will rather than travelling as NaN and coming back as a refusal.
        max_files: numberOr(maxFiles, 1),
        max_file_mb: numberOr(maxFileMb, 10),
        max_attempts: numberOr(maxAttempts, 0),
        late_policy: latePolicy,
      });
      setSaved(true);
      load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  }

  if (error && !assignment) {
    return (
      <div className="stack" style={{ paddingTop: 20 }}>
        <div className="notice notice--accent">{error}</div>
        <Link to="/admin" className="back">
          ← Курсы
        </Link>
      </div>
    );
  }

  if (!assignment) return <p className="muted">Загружаем…</p>;

  const nothingAccepted = !allowText && !allowFiles;

  return (
    <>
      <div className="page-head">
        <Link to={`/admin/lessons/${assignment.lesson_id}`} className="back">
          ← К уроку
        </Link>
        <h1>{assignment.title}</h1>
      </div>

      {error ? <p className="error">{error}</p> : null}
      {saved ? <div className="notice notice--done">Сохранено.</div> : null}

      <section className="editcard stack">
        <div className="field">
          <label htmlFor="a-title">Название</label>
          <input
            id="a-title"
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="a-desc">Что нужно сделать</label>
          <textarea
            id="a-desc"
            className="input"
            rows={5}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Можно **выделять** и писать `код`"
          />
        </div>

        <div className="formrow">
          <div className="field">
            <label htmlFor="a-due">Срок сдачи</label>
            <input
              id="a-due"
              className="input"
              type="datetime-local"
              value={due}
              onChange={(e) => setDue(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="a-late">Если сдали позже срока</label>
            <select
              id="a-late"
              className="input"
              value={latePolicy}
              onChange={(e) => setLatePolicy(e.target.value)}
            >
              <option value="allow_marked">Принимать и помечать как позднюю</option>
              <option value="deny">Не принимать</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="a-att">Попыток (0 — без ограничения)</label>
            <input
              id="a-att"
              className="input"
              type="number"
              min={0}
              max={50}
              value={maxAttempts}
              onChange={(e) => setMaxAttempts(e.target.value)}
            />
          </div>
        </div>

        <strong>Что принимаем</strong>
        <label className="checkline">
          <input
            type="checkbox"
            checked={allowText}
            onChange={(e) => setAllowText(e.target.checked)}
          />
          <span>Текстовый ответ</span>
        </label>
        <label className="checkline">
          <input
            type="checkbox"
            checked={allowFiles}
            onChange={(e) => setAllowFiles(e.target.checked)}
          />
          <span>Файлы</span>
        </label>

        {allowFiles ? (
          <div className="formrow">
            <div className="field">
              <label htmlFor="a-files">Сколько файлов</label>
              <input
                id="a-files"
                className="input"
                type="number"
                min={1}
                max={20}
                value={maxFiles}
                onChange={(e) => setMaxFiles(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="a-mb">Размер файла, МБ</label>
              <input
                id="a-mb"
                className="input"
                type="number"
                min={1}
                max={200}
                value={maxFileMb}
                onChange={(e) => setMaxFileMb(e.target.value)}
              />
            </div>
          </div>
        ) : null}

        {nothingAccepted ? (
          <p className="error">Нужно оставить хотя бы одно: иначе сдать работу будет нечем.</p>
        ) : null}

        <div>
          <button
            type="button"
            className="btn"
            disabled={busy || nothingAccepted || title.trim().length === 0}
            onClick={() => void save()}
          >
            {busy ? 'Сохраняем…' : 'Сохранить задание'}
          </button>
        </div>
      </section>
    </>
  );
}
